// Node type definitions and colors for Connection Creator

import {
    BreakerIcon,
    EndIcon,
    HttpIcon,
    MirrorIcon,
    ModifyIcon,
    RepeaterIcon,
    SetIcon,
    StartIcon,
    SubScenarioIcon,
    WaitIcon,
    WebhookIcon,
} from "components/CC/Nodes/NodeIcons";
import { NodeCategory, NodeType } from "components/CC/types/enums";
import { FC } from "react";

// =============================================================================
// Node Registry - Single source of truth for all node metadata
// =============================================================================

export interface NodeRegistryEntry {
    color: string;
    title: string;
    icon: FC<{ className?: string }>;
    category?: NodeCategory;
    /** Whether this node appears in the methods menu */
    inMenu: boolean;
}

/**
 * Central registry of all node types with their metadata.
 * Used by both the node components and the methods menu.
 */
export const NODE_REGISTRY: Record<NodeType, NodeRegistryEntry> = {
    // Flow control nodes
    [NodeType.Wait]: {
        color: "#FAAD14",
        title: "nodes.wait.title",
        icon: WaitIcon,
        category: NodeCategory.FlowControl,
        inMenu: true,
    },
    [NodeType.Repeater]: {
        color: "#FAAD14",
        title: "nodes.repeater.title",
        icon: RepeaterIcon,
        category: NodeCategory.FlowControl,
        inMenu: false,
    },

    // Data operations nodes
    [NodeType.Modify]: {
        color: "#2F54EB",
        title: "nodes.modify.title",
        icon: ModifyIcon,
        category: NodeCategory.DataOperations,
        inMenu: true,
    },
    [NodeType.Set]: {
        color: "#2F54EB",
        title: "nodes.set.title",
        icon: SetIcon,
        category: NodeCategory.DataOperations,
        inMenu: true,
    },

    // Connections nodes
    [NodeType.Http]: {
        color: "#13C2C2",
        title: "nodes.http.title",
        icon: HttpIcon,
        category: NodeCategory.Connections,
        inMenu: true,
    },
    [NodeType.Webhook]: {
        color: "#13C2C2",
        title: "nodes.webhook.title",
        icon: WebhookIcon,
        category: NodeCategory.Connections,
        inMenu: true,
    },

    // Composition nodes (SubScenario, Mirror, End, Breaker)
    [NodeType.SubScenario]: {
        color: "#EB2F96",
        title: "nodes.sub_scenario.title",
        icon: SubScenarioIcon,
        category: NodeCategory.Composition,
        inMenu: false, // TODO: BE not implemented yet
    },
    [NodeType.Mirror]: {
        color: "#2F54EB",
        title: "nodes.mirror.title",
        icon: MirrorIcon,
        category: NodeCategory.Composition,
        inMenu: false,
    },
    [NodeType.End]: {
        color: "#DB1F35",
        title: "nodes.end.title",
        icon: EndIcon,
        category: NodeCategory.Composition,
        inMenu: true,
    },
    [NodeType.Breaker]: {
        color: "#DB1F35",
        title: "nodes.breaker.title",
        icon: BreakerIcon,
        category: NodeCategory.Composition,
        inMenu: true,
    },

    // Start (badge determined by formData.flow, not in connection menu - only in Add Start menu)
    [NodeType.Start]: {
        color: "#008080",
        title: "nodes.start.title",
        icon: StartIcon,
        category: NodeCategory.Composition,
        inMenu: false,
    },

    // Methods (placeholder, not in menu)
    [NodeType.Methods]: {
        color: "#008080",
        title: "nodes.methods.title",
        icon: StartIcon,
        inMenu: false,
    },
};

// =============================================================================
// Helper functions
// =============================================================================

/**
 * Get all nodes that should appear in the methods menu, grouped by category.
 */
export const getMenuNodesByCategory = (): {
    category: NodeCategory;
    nodes: { type: NodeType; entry: NodeRegistryEntry }[];
}[] => {
    const menuNodes = Object.entries(NODE_REGISTRY)
        .filter(([, entry]) => entry.inMenu && entry.category)
        .map(([type, entry]) => ({ type: type as NodeType, entry }));

    const categoryOrder = [
        NodeCategory.FlowControl,
        NodeCategory.DataOperations,
        NodeCategory.Connections,
        NodeCategory.Composition,
    ];

    return categoryOrder.map((category) => ({
        category,
        nodes: menuNodes.filter(({ entry }) => entry.category === category),
    }));
};

/**
 * Get a node entry by its type.
 */
export const getNodeEntry = (type: NodeType): NodeRegistryEntry => NODE_REGISTRY[type];
